import type {RlStandardIdT} from '@freckle/educator-entities/ts/common/types/rl-standard-id'
import type {
  TaggedElaQuestionAttrs,
  ElaQuestionParseInput
} from '@freckle/educator-entities/ts/ela/common/models/questions'
import {parseElaQuestion} from '@freckle/educator-entities/ts/ela/common/models/questions'
import type {MathQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions'
import {parseMathQuestion} from '@freckle/educator-entities/ts/math/common/models/questions'
import {curriculaClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {curriculaPaths} from '@freckle/educator-entities/ts/openapi-clients'
import type {QuestionAlignment} from '@freckle/classroom/ts/common/models/question-alignment'
import {parseQuestionAlignment} from '@freckle/classroom/ts/common/models/question-alignment'
import type {QuestionWorkedExamples} from '@freckle/classroom/ts/common/models/question-worked-examples'
import {parseQuestionWorkedExamples} from '@freckle/classroom/ts/common/models/question-worked-examples'

export type QuestionResponseT = MathQuestionResponseT | ElaQuestionResponseT | null

export type MathQuestionResponseT = {
  tag: 'math'
  questions: MathQuestionAttrs[]
  alignments: QuestionAlignment[]
  workedExamples?: QuestionWorkedExamples[]
}

export type ElaQuestionResponseT = {
  tag: 'ela'
  questions: TaggedElaQuestionAttrs[]
  alignments: QuestionAlignment[]
}

export async function fetchSampleQuestion(
  lang: string,
  contentArea: curriculaPaths['/questions']['get']['parameters']['query']['content-area'],
  standardSetId: string,
  standard: RlStandardIdT
): Promise<QuestionResponseT> {
  const res = await curriculaClient
    .GET('/questions', {
      params: {
        query: {
          language: lang,
          'content-area': contentArea,
          'standard-set': standardSetId,
          standard,
          sample: true,
          expand: ['alignment', 'workedExamples']
        }
      }
    })
    .then(extractData)

  if (res.questions.length === 0) return null

  if (contentArea === 'math')
    return {
      tag: 'math',
      questions: res.questions.map(q => parseMathQuestion(q.value)),
      alignments: res.alignment?.map(a => parseQuestionAlignment(a)),
      workedExamples: res.workedExamples?.map(w => parseQuestionWorkedExamples(w))
    } as MathQuestionResponseT

  return {
    tag: 'ela',
    questions: res.questions.map(q => parseElaQuestion(q.value as ElaQuestionParseInput)),
    alignments: res.alignment?.map(a => parseQuestionAlignment(a)) ?? []
  } as ElaQuestionResponseT
}
